import { useState } from 'react'
import type { ReactNode } from 'react'
import { absoluteDateTime } from '../domain/format.js'
import {
  blankLike, controlKindFor, filledCount, isBag, isBlank, isTechnicalKey, labelForKey, parseMoment, summaryOf, textOf,
  type Bag, type TechnicalEntry,
} from '../domain/humane.js'
import { useI18n } from '../i18n/context.js'
import { Switch, TextArea, TextField } from '../ui/fields.js'
import { Button, Icon, IconButton, cx } from '../ui/primitives.js'
import { MediaFieldControl } from './MediaFieldControl.js'
import { RichTextEditor } from './RichTextEditor.js'
import './humane.css'

interface Common {
  disabled?: boolean
  /** Il formato del testo dichiarato altrove nel documento (`extra.bodyFormat`). */
  bodyFormat?: string
}

/**
 * Un dato qualunque, mostrato come lo compilerebbe una persona: gruppi che si
 * aprono, righe che si aggiungono e si spostano, interruttori, date, immagini
 * con anteprima, testo formattato. Non mostra mai JSON né «null»: un valore
 * assente è un campo vuoto con il suo segnaposto. Le chiavi di servizio non
 * compaiono qui, stanno in «Dettagli tecnici».
 */
export function ValueEditor({ name, label: labelOverride, value, onChange, siblings = {}, depth = 0, omit, ...common }: Common & {
  name: string
  /** Il nome dichiarato dal tipo di contenuto, quando c'è: vince su quello ricavato dalla chiave. */
  label?: string
  value: unknown
  onChange: (value: unknown) => void
  siblings?: Bag
  depth?: number
  /** Chiavi dell'oggetto che un'altra sezione della pagina già modifica (per esempio `seo`). */
  omit?: ReadonlySet<string>
}) {
  const { t } = useI18n()
  const label = labelOverride ?? labelForKey(t, name)

  if (isBag(value)) return <ObjectGroup label={label} value={value} onChange={onChange} depth={depth} omit={omit} {...common}/>
  if (Array.isArray(value)) return <ListGroup name={name} label={label} value={value} onChange={onChange} depth={depth} {...common}/>
  return <ScalarControl name={name} label={label} value={value} onChange={onChange} siblings={siblings} {...common}/>
}

/** Il contenuto di un oggetto senza il riquadro attorno: per chi ha già il suo titolo (una sezione della pagina). */
export function ObjectFields({ value, onChange, depth = 0, omit, ...common }: Common & {
  value: Bag
  onChange: (value: Bag) => void
  depth?: number
  omit?: ReadonlySet<string>
}) {
  const keys = Object.keys(value).filter((key) => !isTechnicalKey(key) && !omit?.has(key))
  // Prima i campi semplici, poi i gruppi e gli elenchi: si compila dall'alto e le sezioni lunghe restano in fondo.
  const composite = (key: string) => isBag(value[key]) || Array.isArray(value[key])
  const ordered = [...keys.filter((key) => !composite(key)), ...keys.filter(composite)]
  return <div className="form-stack">
    {ordered.map((key) => <ValueEditor
      key={key}
      name={key}
      value={value[key]}
      siblings={value}
      depth={depth + 1}
      onChange={(next) => onChange({ ...value, [key]: next })}
      {...common}
    />)}
  </div>
}

export function GroupBox({ label, hint, depth, children, defaultOpen, actions, onOpenChange }: {
  label: string
  hint?: string
  depth: number
  children: ReactNode
  defaultOpen?: boolean
  actions?: ReactNode
  onOpenChange?: (open: boolean) => void
}) {
  const [open, setOpen] = useState(defaultOpen ?? depth <= 1)
  return <details className={cx('hgroup', depth > 1 && 'is-nested')} open={open} onToggle={(event) => { const next = (event.currentTarget as HTMLDetailsElement).open; setOpen(next); onOpenChange?.(next) }}>
    <summary className="hgroup-summary">
      <Icon name={open ? 'expand_less' : 'expand_more'} className="hgroup-chevron"/>
      <span className="hgroup-title">{label}</span>
      {hint && <span className="hgroup-hint">{hint}</span>}
      {actions && <span className="hgroup-actions" onClick={(event) => event.preventDefault()}>{actions}</span>}
    </summary>
    <div className="hgroup-body">{children}</div>
  </details>
}

function ObjectGroup({ label, value, onChange, depth, omit, ...common }: Common & {
  label: string
  value: Bag
  onChange: (value: unknown) => void
  depth: number
  omit?: ReadonlySet<string>
}) {
  const { t } = useI18n()
  const count = filledCount(value)
  const empty = Object.keys(value).filter((key) => !isTechnicalKey(key) && !omit?.has(key)).length === 0
  return <GroupBox label={label} depth={depth} hint={count ? t('editor.filled', { count }) : undefined} defaultOpen={depth <= 1}>
    {empty
      ? <p className="muted">{t('editor.noValue')}</p>
      : <ObjectFields value={value} onChange={onChange} depth={depth} omit={omit} {...common}/>}
  </GroupBox>
}

function ListGroup({ name, label, value, onChange, depth, disabled, bodyFormat }: Common & {
  name: string
  label: string
  value: unknown[]
  onChange: (value: unknown) => void
  depth: number
}) {
  const { t } = useI18n()
  const items = value
  const simple = items.every((item) => !isBag(item) && !Array.isArray(item))

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    const a = items[index]
    const b = items[target]
    if (target < 0 || target >= items.length || a === undefined || b === undefined) return
    const next = [...items]
    next[index] = b
    next[target] = a
    onChange(next)
  }
  const add = () => {
    const sample = items.find((item) => item !== null && item !== undefined)
    onChange([...items, sample === undefined ? '' : blankLike(sample)])
  }
  const rowTools = (index: number) => <>
    <IconButton icon="arrow_upward" label={t('editor.moveUp')} onClick={() => move(index, -1)} disabled={disabled || index === 0}/>
    <IconButton icon="arrow_downward" label={t('editor.moveDown')} onClick={() => move(index, 1)} disabled={disabled || index === items.length - 1}/>
    <IconButton icon="delete" label={t('editor.removeItem')} onClick={() => onChange(items.filter((_, i) => i !== index))} disabled={disabled}/>
  </>
  const replaceAt = (index: number) => (next: unknown) => onChange(items.map((current, i) => (i === index ? next : current)))

  return <GroupBox label={label} depth={depth} hint={items.length ? t('editor.filled', { count: items.length }) : undefined} defaultOpen={depth <= 1 || items.length <= 3}>
    {items.length === 0 && <p className="muted">{t('editor.noItems')}</p>}
    <div className="hlist">
      {items.map((item, index) => simple
        ? <div className="hlist-row" key={index}>
          <ScalarControl name={name} label={t('editor.itemNumber', { number: index + 1 })} hideLabel value={item} siblings={{}} disabled={disabled} bodyFormat={bodyFormat} onChange={replaceAt(index)}/>
          <span className="hlist-tools">{rowTools(index)}</span>
        </div>
        : <GroupBox key={index} depth={depth + 2} defaultOpen={items.length <= 3 && depth <= 1}
          label={summaryOf(item) || t('editor.itemNumber', { number: index + 1 })}
          hint={summaryOf(item) ? t('editor.itemNumber', { number: index + 1 }) : undefined}
          actions={!disabled ? rowTools(index) : undefined}>
          {isBag(item)
            ? <ObjectFields value={item} depth={depth + 1} disabled={disabled} bodyFormat={bodyFormat} onChange={replaceAt(index)}/>
            : <ValueEditor name={String(index)} value={item} depth={depth + 2} disabled={disabled} bodyFormat={bodyFormat} onChange={replaceAt(index)}/>}
        </GroupBox>)}
    </div>
    {!disabled && <div><Button variant="tonal" small icon="add" onClick={add}>{t('editor.addItem')}</Button></div>}
  </GroupBox>
}

/** Data e, se il dato l'aveva, ora: testo ISO e millisecondi tornano nel formato in cui erano. */
function DateControl({ label, value, onChange, disabled }: { label: string; value: unknown; onChange: (value: unknown) => void; disabled?: boolean }) {
  const { t } = useI18n()
  const moment = parseMoment(value)
  const asNumber = typeof value === 'number'
  const withTime = asNumber || (typeof value === 'string' && value.trim().length > 10)
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateText = moment ? `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}` : typeof value === 'string' ? value.slice(0, 10) : ''
  const timeText = moment && withTime ? `${pad(moment.getHours())}:${pad(moment.getMinutes())}` : ''

  const compose = (date: string, time: string) => {
    if (!date) { onChange(''); return }
    if (!withTime) { onChange(date); return }
    const local = new Date(`${date}T${time || '00:00'}:00`)
    if (Number.isNaN(local.getTime())) return
    onChange(asNumber ? local.getTime() : local.toISOString())
  }

  return <div className="field">
    <div className="field-label-row"><span className="field-label">{label}</span></div>
    <div className="hdate">
      <input type="date" className="field-input hdate-input" aria-label={label} value={dateText} disabled={disabled} onChange={(event) => compose(event.target.value, timeText)}/>
      {withTime && <input type="time" className="field-input hdate-input" aria-label={t('editor.timeLabel')} value={timeText} disabled={disabled} onChange={(event) => compose(dateText, event.target.value)}/>}
      {!disabled && dateText && <IconButton icon="close" label={t('editor.clearDate')} onClick={() => onChange('')}/>}
    </div>
  </div>
}

function ScalarControl({ name, label, value, onChange, siblings, disabled, bodyFormat, hideLabel }: Common & {
  name: string
  label: string
  value: unknown
  onChange: (value: unknown) => void
  siblings: Bag
  hideLabel?: boolean
}) {
  const { t } = useI18n()
  const kind = controlKindFor(name, value, siblings, bodyFormat)
  const text = textOf(value)

  switch (kind) {
    case 'switch':
      return <div className="field"><Switch label={label} checked={value === true} disabled={disabled} onChange={(event) => onChange(event.target.checked)}/></div>
    case 'number':
      return <TextField label={label} hideLabel={hideLabel} type="number" value={text} disabled={disabled} placeholder={t('editor.emptyPlaceholder')}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}/>
    case 'date':
      return <DateControl label={label} value={value} onChange={onChange} disabled={disabled}/>
    case 'html':
      return <RichTextEditor label={label} value={text} disabled={disabled} onChange={onChange}/>
    case 'image':
      return <MediaFieldControl label={label} value={value} disabled={disabled} onChange={onChange}/>
    case 'url':
      return <TextField label={label} hideLabel={hideLabel} leadingIcon="link" value={text} disabled={disabled} placeholder={t('editor.urlPlaceholder')}
        onChange={(event) => onChange(event.target.value)}
        supporting={/^https?:\/\//i.test(text) ? <a href={text} target="_blank" rel="noopener noreferrer">{t('editor.openLink')}</a> : undefined}/>
    case 'longtext':
      return <TextArea label={label} hideLabel={hideLabel} rows={3} value={text} disabled={disabled} placeholder={t('editor.writeHere')} onChange={(event) => onChange(event.target.value)}/>
    default:
      return <TextField label={label} hideLabel={hideLabel} value={text} disabled={disabled} placeholder={t('editor.emptyPlaceholder')} onChange={(event) => onChange(event.target.value)}/>
  }
}

// ----- Sola lettura ---------------------------------------------------------------------

function ReadOnlyValue({ value, locale }: { value: unknown; locale: string }): ReactNode {
  const { t } = useI18n()
  if (isBlank(value)) return <span className="muted">{t('editor.noValue')}</span>
  if (typeof value === 'boolean') return <>{value ? t('editor.yes') : t('editor.no')}</>
  const moment = parseMoment(value)
  if (moment) return <>{absoluteDateTime(moment.getTime(), locale)}</>
  if (Array.isArray(value)) {
    return value.every((item) => !isBag(item) && !Array.isArray(item))
      ? <>{value.map((item) => textOf(item)).filter(Boolean).join(', ')}</>
      : <ul className="hreadonly-list">{value.map((item, index) => <li key={index}><ReadOnlyValue value={item} locale={locale}/></li>)}</ul>
  }
  if (isBag(value)) return <ReadOnlyBag value={value} locale={locale}/>
  return <>{textOf(value)}</>
}

function ReadOnlyBag({ value, locale }: { value: Bag; locale: string }) {
  const { t } = useI18n()
  const rows = Object.entries(value).filter(([, child]) => !isBlank(child))
  if (rows.length === 0) return <span className="muted">{t('editor.noValue')}</span>
  return <dl className="hreadonly">
    {rows.map(([key, child]) => <div key={key}>
      <dt>{labelForKey(t, key)}</dt>
      <dd><ReadOnlyValue value={child} locale={locale}/></dd>
    </div>)}
  </dl>
}

/** «Dettagli tecnici»: i dati di servizio, in parole, senza campi da toccare. */
export function TechnicalDetails({ entries }: { entries: readonly TechnicalEntry[] }) {
  const { t, locale } = useI18n()
  if (entries.length === 0) return null
  return <dl className="hreadonly">
    {entries.map((entry) => <div key={entry.path.join('.')}>
      <dt>{entry.path.map((segment) => labelForKey(t, segment)).join(' › ')}</dt>
      <dd><ReadOnlyValue value={entry.value} locale={locale}/></dd>
    </div>)}
  </dl>
}
